import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Button from '@ui-elements/Button';
import { format, parseISO } from 'date-fns';
import { UserJudgment, SurveyRejectReason } from '@mytypes';
import { JUDGEMENT_STATUS, JudgementStatus } from '~/common/constants';
import {
  ANTISOCIAL_CHECK_NAME,
  ANTISOCIAL_CHECK_APPROVAL_NAME,
  CREDIT_CHECK_NAME,
  SURVEY_CHECK_NAME,
  SURVEY_STATUSES,
} from '~/constants';
import { PALETTE, FONT_SIZES, FONT_WEIGHTS, JUDGEMENT_STATUS_COLORS } from '~/assets/styles/base';

const Wrapper = styled.div`
  padding: 15px 17px 17px;
  margin-bottom: 18px;
  background-color: #e2eef9;
  min-height: 201px;
`;

const Title = styled.div`
  font-size: ${FONT_SIZES.title3};
  font-weight: ${FONT_WEIGHTS.size2};
  margin-bottom: 13px;
`;

const FlexBox = styled.div`
  display: flex;
  justify-content: space-evenly;
`;

const StatusBox = styled(Box)`
  min-width: 220px;
  width: 100%;
  min-height: 100px;
  background-color: ${PALETTE.white};
  padding: 17px 16px 18px;
  margin-right: 17px;
  &:last-child {
    margin-right: 0;
  }
`;

const StatusBoxHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0 8px;
  border-bottom: 1px solid ${PALETTE.border.frame};
  min-height: 27px;
`;

type LabelProps = {
  $useCreditCard?: boolean;
};
const Label = styled.div<LabelProps>`
  font-size: ${FONT_SIZES.title4};
  opacity: ${({ $useCreditCard }) => $useCreditCard && 0.5};
`;

type StatusProps = {
  $status: JudgementStatus;
};
const Status = styled.div<StatusProps>`
  font-size: 1.8rem;
  font-weight: ${FONT_WEIGHTS.size2};
  color: ${({ $status }) => JUDGEMENT_STATUS_COLORS[$status]};
`;

type StatusBoxBodyProps = {
  $useCreditCard?: boolean;
};
const StatusBoxBody = styled.div<StatusBoxBodyProps>`
  padding-top: 14px;
  display: ${({ $useCreditCard }) => ($useCreditCard ? 'block' : 'flex')};
  align-items: center;
  justify-content: space-between;
`;

const StyledButton = styled(Button)`
  font-size: ${FONT_SIZES.title5};
  background-color: ${PALETTE.blue.light};
  margin-left: auto;
  &.MuiButton-root {
    border: 1px solid #a2b2be52;
    box-shadow: 1px 1px 2px #0000000b;
    border-radius: 18px;
    min-height: 26px;
    width: 80px;
    min-width: 80px;
    padding: 0;
    color: ${PALETTE.text.main};
    font-weight: ${FONT_WEIGHTS.base};
    &:hover {
      background-color: #f5faff;
    }
    &:disabled {
      opacity: 0.5;
    }
  }
`;

const Name = styled.div`
  font-size: ${FONT_SIZES.body4};
  line-height: 1.4;
  padding: 0 5px 4px 0;
`;

const Date = styled.div`
  font-size: ${FONT_SIZES.note1};
  color: #73899c;
  margin-bottom: 8px;
`;

const Note = styled.div`
  font-size: ${FONT_SIZES.body3};
  font-weight: ${FONT_WEIGHTS.base};
  color: #062f50;
  opacity: 0.5;
  text-align: center;
  margin-top: 6px;
`;

const SurveyRejectHistory = styled.div`
  width: 120px;
  text-align: right;
`;

type Props = {
  judgement: UserJudgment | null;
  surveyRejectHistory?: SurveyRejectReason[];
  surveyStatusId: number;
  setIsModalOpenAntisocialCheck: React.Dispatch<React.SetStateAction<boolean>>;
  setIsModalOpenAntisocialCheckApproval: React.Dispatch<React.SetStateAction<boolean>>;
  setIsModalOpenCreditCheck: React.Dispatch<React.SetStateAction<boolean>>;
  setIsModalOpenSurveyCheck: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenModalSurveyRejectHistory: React.Dispatch<React.SetStateAction<boolean>>;
};

const SurveyJudgementBox: React.FC<Props> = ({
  judgement,
  surveyRejectHistory,
  surveyStatusId,
  setIsModalOpenAntisocialCheck,
  setIsModalOpenAntisocialCheckApproval,
  setIsModalOpenCreditCheck,
  setIsModalOpenSurveyCheck,
  setIsOpenModalSurveyRejectHistory,
}): JSX.Element => {
  const { t } = useTranslation();

  const getSurveyCheckJudgementStatus = useMemo(() => {
    if (SURVEY_STATUSES.APPROVED <= surveyStatusId && surveyStatusId <= SURVEY_STATUSES.COMPLETED) {
      return JUDGEMENT_STATUS.OK;
    }
    if (surveyStatusId === SURVEY_STATUSES.REJECTED) {
      return JUDGEMENT_STATUS.NG;
    }
    return JUDGEMENT_STATUS.UNTOUCHED;
  }, [surveyStatusId]);

  // initialStateはnullなのでstate内容が入る前にコンテナだけ用意
  if (judgement === null) {
    return <Wrapper data-testid="uiParts-SurveyJudgementBox-EmptyContainer" />;
  }

  const { antisocialCheck, antisocialCheckApproval, creditCheck, surveyCheck } = judgement;
  const useCreditCard = creditCheck.status === JUDGEMENT_STATUS.UNNEEDED;

  return (
    <Wrapper data-testid="uiParts-SurveyJudgementBox">
      <Title>{t('adminSurvey.JudgementBox.title')}</Title>

      <FlexBox>
        <StatusBox data-testid="uiParts-SurveyJudgementBox-antisocialCheck">
          <StatusBoxHead>
            <Label>{t('adminSurvey.JudgementBox.antisocialCheck.label')}</Label>
            <Status $status={antisocialCheck.status}>
              {(ANTISOCIAL_CHECK_NAME as Record<number, string>)[antisocialCheck.status]}
            </Status>
          </StatusBoxHead>
          <StatusBoxBody>
            {antisocialCheck.status !== JUDGEMENT_STATUS.UNTOUCHED && (
              <div>
                <Name>{antisocialCheck.adminName}</Name>
                <Date>
                  {antisocialCheck.checkTime &&
                    format(parseISO(antisocialCheck.checkTime), 'yyyy/MM/dd HH:mm')}
                </Date>
              </div>
            )}
            <StyledButton
              size="small"
              colorType="gray"
              onClick={() => setIsModalOpenAntisocialCheck(true)}
              data-testid="JudgementBox-antisocialCheck-StatusChangeBtn"
              disabled={!antisocialCheck.editable}
            >
              {t('adminSurvey.JudgementBox.editButton')}
            </StyledButton>
          </StatusBoxBody>
        </StatusBox>
        <StatusBox data-testid="uiParts-SurveyJudgementBox-antisocialCheckApproval">
          <StatusBoxHead>
            <Label>{t('adminSurvey.JudgementBox.antisocialCheckApproval.label')}</Label>

            {/* 反社チェックNGの場合は承認は「不要」になる */}
            {antisocialCheck.status === JUDGEMENT_STATUS.NG ? (
              <Status $status={JUDGEMENT_STATUS.UNNEEDED}>
                {ANTISOCIAL_CHECK_APPROVAL_NAME[JUDGEMENT_STATUS.UNNEEDED]}
              </Status>
            ) : (
              <Status $status={antisocialCheckApproval.status}>
                {ANTISOCIAL_CHECK_APPROVAL_NAME[antisocialCheckApproval.status]}
              </Status>
            )}
          </StatusBoxHead>
          <StatusBoxBody>
            {antisocialCheckApproval.status !== JUDGEMENT_STATUS.UNTOUCHED && (
              <div>
                <Name>{antisocialCheckApproval.adminName}</Name>
                <Date>
                  {antisocialCheckApproval.approvalTime &&
                    format(parseISO(antisocialCheckApproval.approvalTime), 'yyyy/MM/dd HH:mm')}
                </Date>
              </div>
            )}
            {/* 反社チェックNGの場合は承認はボタンは表示しない */}
            {antisocialCheck.status !== JUDGEMENT_STATUS.NG && (
              <StyledButton
                size="small"
                colorType="gray"
                onClick={() => setIsModalOpenAntisocialCheckApproval(true)}
                data-testid="JudgementBox-antisocialCheckApproval-StatusChangeBtn"
                disabled={!antisocialCheckApproval.editable}
              >
                {t('adminSurvey.JudgementBox.editButton')}
              </StyledButton>
            )}
          </StatusBoxBody>
        </StatusBox>

        <StatusBox data-testid="uiParts-SurveyJudgementBox-creditCheck">
          <StatusBoxHead>
            <Label $useCreditCard={useCreditCard}>
              {t('adminSurvey.JudgementBox.creditCheck.label')}
            </Label>
            {!useCreditCard && (
              <Status $status={creditCheck.status}>{CREDIT_CHECK_NAME[creditCheck.status]}</Status>
            )}
          </StatusBoxHead>
          <StatusBoxBody $useCreditCard={useCreditCard}>
            {useCreditCard ? (
              <Note>
                <div>{t('adminSurvey.JudgementBox.creditCheck.note')}</div>
              </Note>
            ) : (
              <>
                <div>
                  <Name>{creditCheck.adminName}</Name>
                  <Date>
                    {creditCheck.checkTime &&
                      format(parseISO(creditCheck.checkTime), 'yyyy/MM/dd HH:mm')}
                  </Date>
                </div>
                <StyledButton
                  key="creditCheck-editable"
                  size="small"
                  colorType="gray"
                  onClick={() => setIsModalOpenCreditCheck(true)}
                  data-testid="JudgementBox-creditCheck-StatusChangeBtn"
                  disabled={!creditCheck.editable}
                >
                  {t('adminSurvey.JudgementBox.editButton')}
                </StyledButton>
              </>
            )}
          </StatusBoxBody>
        </StatusBox>

        <StatusBox data-testid="uiParts-SurveyJudgementBox-surveyCheck">
          <StatusBoxHead>
            <Label>{t('adminSurvey.JudgementBox.surveyCheck.label')}</Label>
            <Status $status={getSurveyCheckJudgementStatus}>
              {SURVEY_CHECK_NAME[getSurveyCheckJudgementStatus]}
            </Status>
          </StatusBoxHead>
          <StatusBoxBody>
            {surveyRejectHistory && (
              <SurveyRejectHistory>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link
                  component="button"
                  variant="body4"
                  color={PALETTE.text.main}
                  onClick={() => setIsOpenModalSurveyRejectHistory(true)}
                >
                  {t('adminSurvey.JudgementBox.surveyRejectHistoryLink')}
                </Link>
              </SurveyRejectHistory>
            )}
            <StyledButton
              size="small"
              colorType="gray"
              onClick={() => setIsModalOpenSurveyCheck(true)}
              disabled={!surveyCheck.editable}
              data-testid="JudgementBox-surveyCheck-StatusChangeBtn"
            >
              {t('adminSurvey.JudgementBox.editButton')}
            </StyledButton>
          </StatusBoxBody>
        </StatusBox>
      </FlexBox>
    </Wrapper>
  );
};

export default SurveyJudgementBox;
